import { Expose, Type, Exclude } from 'class-transformer';
import { Point } from 'geojson';

import { Default, ExposeRawData } from '@lib/common/decorators';
import { SnModel } from '@lib/common/models';
import { ISnIsSharable } from '@lib/share';

import { SnContentType, IHasContentType } from '../base';
import { SnOrganization } from '../organization';
import { SnShareInfo } from '../share';
import { SnTeam } from '../team';

export class SnUser extends SnModel implements ISnIsSharable, IHasContentType {
  @Expose()
  public id: string;

  @Expose()
  @Default('')
  public firstName: string;

  @Expose()
  public isExternal: boolean;

  @Expose()
  public isExternallyManaged: boolean;

  @Expose()
  public isActive: boolean;

  @Expose()
  public isLocked: boolean;

  @Expose()
  @Default('')
  public lastName: string;

  @Expose()
  public email: string;

  @Expose()
  public mobilePhone: string;

  @Expose()
  public isSupportUser: boolean;

  @Expose()
  public badge: string;

  @Expose()
  public title: string;

  @Expose({ name: 'externalOrganization' })
  public company: string;

  @Expose({ name: 'externalOrganizationTitle' })
  public role: string;

  @Expose()
  public notes: string;

  @Expose()
  public officerInternalId: string;

  @Expose({ name: 'referrer' })
  public nominatedBy: string;

  // When fetching the users through a related entity (like policy groups), the organisation is
  // not nested as an object, but just the UUID to the organisation
  @Expose({ name: 'organization' })
  @Exclude({ toPlainOnly: true })
  public organization: SnOrganization;

  @Expose()
  public groups: number[];

  @Expose()
  public jwtRefreshExp: number;

  @Expose()
  public passwordExpired: boolean;

  @Expose()
  public passwordAge: number;

  @Expose()
  public passwordDaysLeft: number | any;

  @Expose()
  public isVideoWallUser: boolean;

  @Expose()
  @Exclude({ toPlainOnly: true })
  public mobileLoggedIn: boolean;

  @Expose()
  @Exclude({ toPlainOnly: true })
  public isLive: boolean;

  @Expose()
  @Type(() => Date)
  @Exclude({ toPlainOnly: true })
  public lastLogin: Date;

  @Expose()
  @Exclude({ toPlainOnly: true })
  public speed: number;

  @Expose()
  @Exclude({ toPlainOnly: true })
  public altitude: number;

  @Expose()
  @Type(() => SnTeam)
  @Exclude({ toPlainOnly: true })
  public team: SnTeam;

  @ExposeRawData()
  public callSign: any;

  @Expose({ name: 'updatedTimestamp' })
  @Type(() => Date)
  @Exclude({ toPlainOnly: true })
  public updatedAt: Date;

  @Expose()
  public expirationDatetime: Date;

  @Expose()
  public expirationTimestamp: string;

  @Expose()
  public canEdit: boolean;

  @Expose()
  public canView: boolean;

  @Expose()
  public isAdmin: boolean;

  @Expose()
  public isShared: boolean;

  @Expose()
  public mfaEnabled: boolean;

  @Expose()
  public isOnShift: boolean;

  @Expose()
  @Type(() => SnShareInfo)
  public shareInfo: SnShareInfo;

  @ExposeRawData()
  public geometry?: Point;

  @Expose()
  public authTokens: any;

  @Expose()
  public axonUserId: string;

  public readonly contentType = SnContentType.User;
  public alertBoundary: any;
  public alertBoundaryGeoJson: any;

  constructor(data: any = {}) {
    super();
    this.fromJSON(data);
  }

  get fullName(): string {
    return `${this.firstName} ${this.lastName}`;
  }

  get isOwned(): boolean {
    return !this.isShared || this.shareInfo.direction === 'Out';
  }
}
